
import { ActionTypes } from '@/store/actionTypes';
import { defineComponent } from 'vue';
import { get } from 'lodash';
import FbxTextarea from '@/components/fbx-textarea.vue';
import FbxButton from '@/components/fbx-button.vue';
import { isNullOrUndefined } from '@/shared/helpers';
import { mapGetters } from 'vuex';
import TrailerAssessment from '../../types/TrailerAssessment';
import TrailerAssessmentSubType from '../../types/TrailerAssessmentSubType';
import globalConstants from '../../shared/globalConstants';
import localforage from 'localforage';
import imageCompression from 'browser-image-compression';
import {
    AssessmentPhotoType,
    AssessmentPhotoSubtype,
} from '@/types/TrailerPhotoAssessmentTypes';

export default defineComponent({
    name: 'DamageComments',
    components: {
        'fbx-textarea': FbxTextarea,
        'fbx-button': FbxButton,
    },
    data(): {
        title: string;
        subTitle: string;
        subType: string;
        textareaModel: string;
        photoTaken: boolean;
        photoName: string | null;
        canvasWidth: number;
        canvasHeight: number;
        tempPhotoBlob: Blob | null;
    } {
        return {
            title: '',
            subTitle: '',
            subType: '',
            textareaModel: '',
            photoTaken: false,
            photoName: null,
            canvasWidth: 0,
            canvasHeight: 0,
            tempPhotoBlob: null,
        };
    },
    props: {
        assessmentType: {
            type: String,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            trailerAssessment: 'trailerAssessment',
            selectedTrailer: 'selectedTrailer',
            hasPhotoForAssessment: 'hasPhotoForAssessment',
        }),
        partialTrailerAssessment: function () {
            if (!this.trailerAssessment) return;
            const assessmentType =
                this.trailerAssessment[
                    this.assessmentType as keyof TrailerAssessment
                ];
            const subType = assessmentType.subTypes.find(
                (t: TrailerAssessmentSubType) => t.subType === this.subType
            );

            if (!isNullOrUndefined(subType)) {
                return subType;
            }

            return null;
        },
        isSaveDisabled: function (): boolean {
            return this.textareaModel.trim() === '';
        },
    },
    async created() {
        this.initializeRouteParams();
        this.initializeComments();
        this.initializeTitle();
        this.initializeSubtitle();
        await this.checkExistingPhoto();
    },
    methods: {
        initializeRouteParams: function (): void {
            this.subType = this.$route.params.subType as string;

            if (isNullOrUndefined(this.subType)) {
                console.error('subType route param is required');
            }
        },
        initializeComments: function () {
            if (!isNullOrUndefined(this.partialTrailerAssessment)) {
                if (!isNullOrUndefined(this.partialTrailerAssessment.comment)) {
                    this.textareaModel = this.partialTrailerAssessment.comment;
                }
            }
        },
        async checkExistingPhoto(): Promise<void> {
            const photoType = this.getPhotoType();
            const photoSubtype = this.getPhotoSubtype();

            const trailerNumber = this.$route.params.trailerNumber as string;

            if (!photoType || !photoSubtype || !trailerNumber) {
                console.warn(
                    'Missing required data - photoType:',
                    photoType,
                    'photoSubtype:',
                    photoSubtype,
                    'trailerNumber:',
                    trailerNumber
                );
                return;
            }

            // Generate the photo name/key with trailer ID
            this.photoName = `${trailerNumber}-${globalConstants.trailer.photos.IDB_PREFIX}${photoType}-${photoSubtype}`;

            // Check IndexedDB directly for existing photo
            try {
                const photoBlob = (await localforage.getItem(
                    this.photoName
                )) as Blob | null;
                if (photoBlob) {
                    this.photoTaken = true;
                    await this.loadExistingPhoto();

                    // Ensure it's also in the Vuex store
                    this.$store.dispatch(ActionTypes.SET_TRAILER_PHOTO, {
                        type: photoType,
                        subtype: photoSubtype,
                        photoName: this.photoName,
                    });
                }
            } catch (error) {
                console.error('Failed to check for existing photo:', error);
            }
        },
        async loadExistingPhoto(): Promise<void> {
            if (!this.photoName) return;

            try {
                const photoBlob = (await localforage.getItem(
                    this.photoName
                )) as Blob;
                if (photoBlob) {
                    const url = await this.getDataUrlFromFile(photoBlob);
                    const img = document.createElement('img');
                    img.onload = () => {
                        this.canvasWidth = img.width;
                        this.canvasHeight = img.height;
                        this.$nextTick(() => {
                            this.drawImageToCanvas(img);
                        });
                    };
                    img.src = url;
                }
            } catch (error) {
                console.error('Failed to load existing photo:', error);
            }
        },
        getPhotoType(): AssessmentPhotoType | null {
            const typeMap: { [key: string]: AssessmentPhotoType } = {
                kingpinLanding: AssessmentPhotoType.KINGPIN_LANDING,
                airlinesElectrical: AssessmentPhotoType.AIRLINES_ELECTRICAL,
                brakes: AssessmentPhotoType.BRAKES,
                tires: AssessmentPhotoType.TIRES,
                lights: AssessmentPhotoType.LIGHTS,
                damage: AssessmentPhotoType.DAMAGE,
                other: AssessmentPhotoType.OTHER,
            };
            return typeMap[this.assessmentType] || null;
        },
        getPhotoSubtype(): AssessmentPhotoSubtype | null {
            const subtypeMap: { [key: string]: AssessmentPhotoSubtype } = {
                kingpin: AssessmentPhotoSubtype.KINGPIN,
                driverSide: AssessmentPhotoSubtype.DRIVER_SIDE,
                passengerSide: AssessmentPhotoSubtype.PASSENGER_SIDE,
                leftAirline: AssessmentPhotoSubtype.LEFT_AIRLINE,
                '7way': AssessmentPhotoSubtype.SEVEN_WAY,
                rightAirline: AssessmentPhotoSubtype.RIGHT_AIRLINE,
                lowerAirlineElectrical:
                    AssessmentPhotoSubtype.LOWER_AIRLINE_ELECTRICAL,
                frontSide: AssessmentPhotoSubtype.FRONT_SIDE,
                rearSide: AssessmentPhotoSubtype.REAR_SIDE,
                driverTop: AssessmentPhotoSubtype.DRIVER_TOP,
                centerTop: AssessmentPhotoSubtype.CENTER_TOP,
                passengerTop: AssessmentPhotoSubtype.PASSENGER_TOP,
                driverBottom: AssessmentPhotoSubtype.DRIVER_BOTTOM,
                centerBottom: AssessmentPhotoSubtype.CENTER_BOTTOM,
                passengerBottom: AssessmentPhotoSubtype.PASSENGER_BOTTOM,
                other: AssessmentPhotoSubtype.OTHER,
            };
            return subtypeMap[this.subType] || null;
        },
        onTakePicture(): void {
            const imageInput = this.$refs.imageInput as HTMLInputElement;
            if (imageInput) {
                imageInput.click();
            }
        },
        async onImageUploaded(event: Event): Promise<void> {
            const input = event.target as HTMLInputElement;
            const file = input.files?.[0];

            if (!file) return;

            try {
                // Compress the image
                const compressed = await imageCompression(
                    file,
                    globalConstants.trailer.photos.compressionOptions
                );

                // Store in memory temporarily (don't save to IndexedDB yet)
                this.tempPhotoBlob = compressed;

                // Display preview
                const url = await this.getDataUrlFromFile(compressed);
                const img = document.createElement('img');
                img.onload = () => {
                    this.canvasWidth = img.width;
                    this.canvasHeight = img.height;
                    this.photoTaken = true;
                    this.$nextTick(() => {
                        this.drawImageToCanvas(img);
                    });
                };
                img.src = url;
            } catch (error) {
                console.error('Failed to process image:', error);
            }
        },
        async saveImage(file: Blob): Promise<void> {
            const photoType = this.getPhotoType();
            const photoSubtype = this.getPhotoSubtype();

            // Get trailer number from store or route params
            const trailerNumber =
                this.selectedTrailer?.trailerNumber ||
                (this.$route.params.trailerNumber as string);

            if (!photoType || !photoSubtype || !trailerNumber) {
                console.error(
                    'Invalid photo type or subtype or no trailer number'
                );
                console.error('Missing:', {
                    photoType: !photoType,
                    photoSubtype: !photoSubtype,
                    trailerNumber: !trailerNumber,
                });
                return;
            }

            // Include trailer ID in the key
            this.photoName = `${trailerNumber}-${globalConstants.trailer.photos.IDB_PREFIX}${photoType}-${photoSubtype}`;

            // Store in IndexedDB
            await localforage.setItem(this.photoName, file);

            // Update Vuex store
            this.$store.dispatch(ActionTypes.SET_TRAILER_PHOTO, {
                type: photoType,
                subtype: photoSubtype,
                photoName: this.photoName,
            });
        },
        async onDeletePhoto(): Promise<void> {
            try {
                // Clear temp photo from memory
                this.tempPhotoBlob = null;

                // If there's a saved photo, remove it from IndexedDB
                if (this.photoName) {
                    await localforage.removeItem(this.photoName);
                    this.photoName = null;
                }

                this.photoTaken = false;

                // Clear canvas
                const canvas = this.$refs.photoCanvas as HTMLCanvasElement;
                if (canvas) {
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }
                }
            } catch (error) {
                console.error('Failed to delete photo:', error);
            }
        },
        drawImageToCanvas(img: HTMLImageElement): void {
            const canvas = this.$refs.photoCanvas as HTMLCanvasElement;
            if (!canvas) return;

            canvas.width = this.canvasWidth;
            canvas.height = this.canvasHeight;

            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            }
        },
        async getDataUrlFromFile(file: Blob): Promise<string> {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        resolve(e.target.result as string);
                    } else {
                        reject(new Error('Failed to read file'));
                    }
                };
                reader.onerror = (e) => reject(e);
                reader.readAsDataURL(file);
            });
        },
        initializeTitle: function (): void {
            const damageOrType =
                this.assessmentType ===
                globalConstants.trailer.assessmentTypes.kingpinLanding
                    ? 'Damage'
                    : this.assessmentType;

            const subTypes = {
                kingpin: 'Kingpin Damage',
                driverSide: `Driver Side ${damageOrType}`,
                passengerSide: `Passenger Side ${damageOrType}`,
                frontSide: `Front Side ${damageOrType}`,
                rearSide: `Rear Side ${damageOrType}`,
                driverBottom: 'Driver Side Rear Turn/Signal Lamp',
                driverTop: 'Driver Side Rear Clearance Lamp',
                passengerBottom: 'Passenger Side Rear Turn/Signal Lamp',
                passengerTop: 'Passenger Side Rear Clearance Lamp',
                centerBottom: 'Rear Bumper Bar Marking',
                centerTop: 'Identification Lamp',
                leftAirline: 'Left Airline Damage',
                '7way': '7 Way Damage',
                rightAirline: 'Right Airline Damage',
                lowerAirlineElectrical: 'Airline/Electrical Damage',
                other: 'Add Damage',
            };

            this.title = get(subTypes, this.subType);
        },
        initializeSubtitle: function (): void {
            this.subTitle =
                this.assessmentType ===
                globalConstants.trailer.assessmentTypes.brakes
                    ? 'Brakes'
                    : this.assessmentType ===
                      globalConstants.trailer.assessmentTypes.tires
                    ? 'Tires & Wheels'
                    : this.assessmentType ===
                      globalConstants.trailer.assessmentTypes.lights
                    ? 'Lights'
                    : this.assessmentType ===
                      globalConstants.trailer.assessmentTypes.kingpinLanding
                    ? 'Kingpin & Landing Gear'
                    : this.assessmentType ===
                      globalConstants.trailer.assessmentTypes.airlinesElectrical
                    ? 'Airlines & Electrical'
                    : this.assessmentType ===
                      globalConstants.trailer.assessmentTypes.other
                    ? 'Other'
                    : 'Body Damage';
        },
        async onSave(): Promise<void> {
            const partialTrailerAssessment = new TrailerAssessmentSubType(
                this.assessmentType as keyof TrailerAssessment,
                this.subType as string
            );

            // Save photo to IndexedDB if there's a new photo
            if (this.tempPhotoBlob) {
                await this.saveImage(this.tempPhotoBlob);
                this.tempPhotoBlob = null; // Clear temp after saving
            }

            if (this.textareaModel !== '') {
                partialTrailerAssessment.comment = this.textareaModel;
                this.$store.dispatch(
                    ActionTypes.SAVE_PARTIAL_TRAILER_ASSESSMENT,
                    partialTrailerAssessment
                );
            } else {
                this.$store.dispatch(
                    ActionTypes.REMOVE_PARTIAL_TRAILER_ASSESSMENT,
                    partialTrailerAssessment
                );
            }

            this.$router.go(-1);
        },
        onCancel: function (): void {
            // Discard any unsaved photo
            this.tempPhotoBlob = null;

            if (
                this.assessmentType ===
                globalConstants.trailer.assessmentTypes.other
            ) {
                this.$store.dispatch(
                    ActionTypes.REMOVE_TRAILER_ASSESSMENT,
                    this.assessmentType
                );
            }

            this.$router.go(-1);
        },
    },
});
